import { useEffect, useRef } from 'react';

export type ModalKeyboardActions = {
  onEnter?: () => void;
  onEscape?: () => void;
};

// Stack of mounted modal-keyboard instances; only the topmost (last opened) handles keys.
const instanceStack: symbol[] = [];

// A focused control inside the modal handles Enter natively; defer to it instead of firing the primary action.
const ENTER_SKIP_SELECTOR =
  'ion-button, button, textarea, input, [contenteditable="true"]';

// Esc/Enter for the topmost mounted modal. Lives in the content component because that's
// what knows its buttons; Modal stays content-agnostic.
export const useModalKeyboard = ({
  onEnter,
  onEscape,
}: ModalKeyboardActions): void => {
  // Refs (not state): the listener subscribes once but must call the latest closure,
  // since ForceError reuses the same instance across chained errors.
  const onEnterRef = useRef(onEnter);
  const onEscapeRef = useRef(onEscape);
  onEnterRef.current = onEnter;
  onEscapeRef.current = onEscape;

  useEffect(() => {
    const instanceId = Symbol('modalKeyboard');
    instanceStack.push(instanceId);

    const isTopmost = (): boolean =>
      instanceStack[instanceStack.length - 1] === instanceId;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || !isTopmost()) {
        return;
      }

      // Any key is swallowed here so bubble-phase shortcuts (which ignore defaultPrevented) never fire while a modal is open.
      event.stopImmediatePropagation();

      if (event.key === 'Escape') {
        // Prevented unconditionally: a background trigger can still hold focus and must not act on Escape natively.
        event.preventDefault();
        onEscapeRef.current?.();
        return;
      }

      const isEnter = event.key === 'Enter' || event.code === 'NumpadEnter';
      if (!isEnter) {
        return;
      }

      const target = event.target as HTMLElement | null;
      // Scope to inside the modal: a background trigger button keeps focus on open and must not swallow Enter.
      if (
        target?.closest('.modal-window') &&
        target.closest(ENTER_SKIP_SELECTOR)
      ) {
        return;
      }

      // Prevented unconditionally: a background trigger can still hold focus and must not act on Enter natively.
      event.preventDefault();
      onEnterRef.current?.();
    };

    // Capture phase so the modal consumes every key before the bubble-phase global shortcuts.
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      const index = instanceStack.lastIndexOf(instanceId);
      if (index !== -1) {
        instanceStack.splice(index, 1);
      }
    };
  }, []);
};
